// `watchPosition` mantém o sensor de GPS "ligado" e vai entregando leituras
// sucessivas conforme o fix melhora, em vez de reiniciar a busca do zero a
// cada chamada (o que `getCurrentPosition` repetido fazia antes) — na
// prática dá leituras mais precisas, principalmente no celular, onde o GPS
// de verdade costuma convergir pra uma boa precisão em 1-3s depois de
// "aquecido". Fica com a melhor leitura (menor `accuracy`, em metros) vista
// dentro da janela de tempo, e sai mais cedo se já achar uma leitura boa o
// bastante (não precisa esperar o resto da janela à toa).
export function capturarMelhorLocalizacao({ duracaoMs = 8000, precisaoAlvo = 20 } = {}) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);

    let melhor = null;
    let watchId = null;
    let finalizado = false;

    const finalizar = () => {
      if (finalizado) return;
      finalizado = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      resolve(melhor);
    };

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const leitura = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
        if (!melhor || leitura.accuracy < melhor.accuracy) melhor = leitura;
        if (melhor.accuracy <= precisaoAlvo) finalizar();
      },
      (err) => {
        // Permissão negada: não tem sentido esperar o resto da janela, ninguém vai
        // autorizar no meio da espera. Timeout/posição indisponível numa leitura
        // isolada não finaliza sozinho — pode vir uma leitura boa na próxima.
        if (err.code === err.PERMISSION_DENIED) finalizar();
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: duracaoMs }
    );

    setTimeout(finalizar, duracaoMs);
  });
}

// Geocoding (endereço -> coordenada) via Nominatim, o serviço de busca do
// próprio OpenStreetMap — gratuito, sem chave de API, mesma linha do resto
// do projeto. Uso é leve (uma busca manual por vez, quando alguém está
// configurando um posto), dentro do que a política de uso deles tolera; não
// dá pra setar um User-Agent customizado a partir do navegador (o browser
// bloqueia isso por segurança), mas o Referer que o navegador já manda
// sozinho é a forma aceita de identificação pra chamada client-side.
export async function buscarEndereco(endereco) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=${encodeURIComponent(endereco)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Não foi possível buscar esse endereço agora.");
  const resultados = await res.json();
  if (!resultados.length) return null;
  return { latitude: Number(resultados[0].lat), longitude: Number(resultados[0].lon), nomeEncontrado: resultados[0].display_name };
}

// CEP sozinho não carrega coordenada nenhuma — só identifica rua/bairro/
// cidade. Por isso o fluxo é em duas etapas: ViaCEP (base oficial dos
// Correios, sempre acerta o nome exato da rua) resolve o CEP num endereço
// por extenso, e só então esse endereço (já preciso, sem erro de digitação)
// é geocodificado no Nominatim — bem mais confiável do que buscar o CEP cru
// direto no Nominatim, que tem cobertura de CEP muito incompleta no Brasil.
export async function buscarEnderecoPorCep(cep, numero) {
  const cepLimpo = (cep || "").replace(/\D/g, "");
  if (cepLimpo.length !== 8) throw new Error("CEP inválido — precisa ter 8 dígitos.");

  const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
  if (!res.ok) throw new Error("Não foi possível consultar esse CEP agora.");
  const dados = await res.json();
  if (dados.erro) throw new Error("CEP não encontrado.");

  const cidadeUf = dados.localidade && dados.uf ? `${dados.localidade} - ${dados.uf}` : dados.localidade;
  const enderecoCompleto = [dados.logradouro, numero, dados.bairro, cidadeUf].filter(Boolean).join(", ");

  const geo = await buscarEndereco(`${enderecoCompleto}, Brasil`);
  if (!geo) {
    throw new Error("Achamos o endereço do CEP, mas não conseguimos localizar no mapa — ajuste arrastando o marcador.");
  }
  return { latitude: geo.latitude, longitude: geo.longitude, enderecoCompleto };
}
